/**
 * CREATIVE DETONATOR - Toast Notifications
 * Displays temporary notification messages
 *
 * Shared module - used by both Dashboard and Generator pages
 * Moved from dashboard/toast.js to shared/toast.js for app-wide usage
 *
 * @version 1.2
 */

import { Sounds } from './sounds.js';

// ============================================================================
// CONFIGURATION
// ============================================================================

const CONFIG = {
  duration: 3000 // Auto-dismiss after 3 seconds
};

// ============================================================================
// SECURITY: HTML escaping to prevent XSS in toast messages
// ============================================================================

/**
 * Escape HTML special characters to prevent XSS
 * @param {string} str - String to escape
 * @returns {string} Escaped string safe for innerHTML
 */
function escapeHtml(str) {
  if (!str || typeof str !== 'string') return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

// ============================================================================
// TOAST MODULE
// ============================================================================

export const Toast = {
  container: null,

  /**
   * Initialize toast container
   */
  init() {
    this.container = document.getElementById('toast-container');
    if (!this.container) {
      this.container = document.createElement('div');
      this.container.id = 'toast-container';
      this.container.className = 'toast-container';
      document.body.appendChild(this.container);
    }
  },

  /**
   * Show a toast notification
   * @param {string} message - Message to display
   * @param {string} type - Toast type: 'info', 'success', 'error', 'warning'
   * @param {string} title - Optional title
   * @returns {HTMLElement} The toast element
   */
  show(message, type = 'info', title = '') {
    if (!this.container) this.init();

    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;

    const icons = {
      success: '\u2713',  // checkmark
      error: '\u2717',    // X
      warning: '\u26A0',  // warning
      info: '\u2139'      // info
    };

    // SECURITY: Escape message and title to prevent XSS
    const safeTitle = escapeHtml(title);
    const safeMessage = escapeHtml(message);

    toast.innerHTML = `
      <div class="toast-icon">${icons[type] || icons.info}</div>
      <div class="toast-content">
        ${safeTitle ? `<div class="toast-title">${safeTitle}</div>` : ''}
        <div class="toast-message">${safeMessage}</div>
      </div>
      <button class="toast-close">\u00D7</button>
    `;

    // Add to container
    this.container.appendChild(toast);

    // Play sound
    if (type === 'success') {
      Sounds.play('success');
    } else if (type === 'error') {
      Sounds.play('error');
    }

    // Animate in
    requestAnimationFrame(() => {
      toast.classList.add('show');
    });

    // Auto dismiss
    const dismiss = () => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    };

    const timeout = setTimeout(dismiss, CONFIG.duration);

    // Close button
    toast.querySelector('.toast-close').addEventListener('click', () => {
      clearTimeout(timeout);
      dismiss();
    });

    return toast;
  },

  /**
   * Show a success toast
   * @param {string} message - Message to display
   * @param {string} title - Optional title (default: 'Success')
   */
  success(message, title = 'Success') {
    return this.show(message, 'success', title);
  },

  /**
   * Show an error toast
   * @param {string} message - Message to display
   * @param {string} title - Optional title (default: 'Error')
   */
  error(message, title = 'Error') {
    return this.show(message, 'error', title);
  },

  /**
   * Show a warning toast
   * @param {string} message - Message to display
   * @param {string} title - Optional title (default: 'Warning')
   */
  warning(message, title = 'Warning') {
    return this.show(message, 'warning', title);
  },

  /**
   * Show an info toast
   * @param {string} message - Message to display
   * @param {string} title - Optional title
   */
  info(message, title = '') {
    return this.show(message, 'info', title);
  }
};

// Global export for non-module contexts
if (typeof window !== 'undefined') {
  window.Toast = Toast;
}
